import React, { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from '../contexts/SnackbarContext';
import { palette as T, alpha, type } from '../design';


const VARIANT_STYLES = {
  success: { border: T.sageGreen, progress: T.sageGreen, icon: '✓' },
  error: { border: T.errorRed, progress: T.errorRed, icon: '✕' },
  info: { border: T.terracotta, progress: T.terracotta, icon: '●' },
};

export function Snackbar({ id, message, variant, duration }) {
  const { removeSnackbar } = useSnackbar();
  const [progress, setProgress] = useState(100);
  const [exiting, setExiting] = useState(false);

  const dismiss = useCallback(() => {
    if (exiting) return;
    setExiting(true);
    setTimeout(() => removeSnackbar(id), 220);
  }, [exiting, id, removeSnackbar]);

  useEffect(() => {
    const intervalMs = 16;
    const step = (100 / duration) * intervalMs;
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev <= step) {
          clearInterval(timer);
          dismiss();
          return 0;
        }
        return prev - step;
      });
    }, intervalMs);

    return () => clearInterval(timer);
  }, [duration, dismiss]);

  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.info;

  return (
    <div
      onClick={dismiss}
      role={variant === 'error' ? 'alert' : 'status'}
      style={{
        background: T.white,
        borderRadius: 16,
        border: `1.5px solid ${styles.border}`,
        boxShadow: '0 8px 28px ' + alpha('espresso', 'shadow'),
        padding: '14px 18px',
        minWidth: 280,
        maxWidth: 400,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        position: 'relative',
        overflow: 'hidden',
        opacity: exiting ? 0 : 1,
        transform: exiting ? 'translateY(12px)' : 'translateY(0)',
        transition: 'opacity 0.2s ease, transform 0.2s ease',
        animation: 'snackbarSlideIn 0.3s ease',
      }}
    >
      <span style={{
        fontSize: type.body,
        fontWeight: 700,
        color: styles.border,
        flexShrink: 0,
        width: 22,
        height: 22,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        border: `1.5px solid ${styles.border}`,
      }}>
        {styles.icon}
      </span>
      <span style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontSize: type.body,
        fontWeight: 500,
        color: T.espresso,
        lineHeight: 1.4,
        flex: 1,
      }}>
        {message}
      </span>
      <span style={{
        fontSize: type.caption,
        color: T.driftwood,
        flexShrink: 0,
        paddingLeft: 4,
      }}>
        ✕
      </span>

      {/* Progress bar */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        height: 3,
        width: `${progress}%`,
        background: styles.progress,
        borderBottomLeftRadius: 16,
        borderBottomRightRadius: progress < 5 ? 16 : 0,
        transition: 'width 0.05s linear',
      }} />

      <style>{`
        @keyframes snackbarSlideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
